import {
  Button,
  DialogContent,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  useTheme,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import DocumentTemplateSinglePlaceholderConfig from "./DocumentTemplateSinglePlaceholderConfig";
import { Props } from "./interfaces";
import { State } from "./interfaces";
import { Container, StyledDialog, DocumentViewerContainer, StyledTableHead } from "./styles";
import { GearIcon } from "../../../../assets/icons";
import ConfirmationChangesDialog from "../../../../containers/ConfirmationChangesDialog/ConfirmationChangesDialog";
import DialogActions from "../../../../containers/DialogActions/DialogActions";
import DialogTitle from "../../../../containers/DialogTitle/DialogTitle";
import LoadingOverlay from "../../../../containers/LoadingOverlay/LoadingOverlay";
import { DocumentTemplatePlaceholderRef } from "../../../../models/document-templates/document-template-placeholder";
import DocumentViewer from "../../../../reusable-components/DocumentViewer/DocumentViewer";
import { DocumentTemplatePlaceholderConfigApiService } from "../../../../services/document-templates/document-template-version-placeholder-config-api.service";

const ConfigureDocumentTemplateVersionPlaceholders = ({
  placeholders,
  handleNotifyParent,
  buttonType,
  template,
}: Props) => {
  const tRP = "components.document_templates.configure_placeholders";
  const { t } = useTranslation();
  const theme = useTheme();

  const INITIAL_STATE: State = useMemo(
    () => ({
      open: false,
      loading: false,
      error: "",
      placeholders,
      CCDOpen: false,
    }),
    [placeholders]
  );

  const [state, setState] = useState<State>({
    ...INITIAL_STATE,
  });

  //#region update placeholder
  const handleUpdatePlaceholder = useCallback(
    (placeholder: DocumentTemplatePlaceholderRef) => {
      const placeholderIndex = state.placeholders.findIndex((_placeholder) => placeholder.id === _placeholder.id);
      const updatedList = state.placeholders;
      updatedList.splice(placeholderIndex, 1, placeholder);

      setState((state) => ({
        ...state,
        placeholders: [...updatedList],
      }));
    },
    [state.placeholders]
  );
  //#endregion

  //#region
  const handlaSaveConfig = useCallback(async () => {
    setState((state) => ({ ...state, loading: true }));
    try {
      const response = await new DocumentTemplatePlaceholderConfigApiService().saveItems([...state.placeholders]);
      if (response.error) {
        setState((state) => ({ ...state, error: response.error }));
      } else {
        setState((state) => ({ ...state, open: false }));
        handleNotifyParent();
      }
    } catch (error) {
      setState((state) => ({ ...state, error: error as string }));
    } finally {
      setState((state) => ({ ...state, loading: false }));
    }
  }, [state.placeholders, handleNotifyParent]);
  //#endregion
  useEffect(() => {
    setState((state) => ({ ...state, placeholders }));
  }, [placeholders]);

  return (
    <>
      {buttonType === "icon" ? (
        <IconButton onClick={() => setState((state) => ({ ...state, open: true }))}>
          <GearIcon />
        </IconButton>
      ) : (
        <Button
          style={{
            color: placeholders.length === 0 ? theme.palette.text.secondary : theme.palette.text.primary,
          }}
          onClick={() => setState((state) => ({ ...state, open: true }))}
          disabled={placeholders.length === 0}
        >{`${t(`${tRP}.button_title`)}`}</Button>
      )}

      <StyledDialog
        open={state.open}
        fullWidth
      >
        <DialogTitle
          title={`${t(`${tRP}.title`)}`}
          handleClose={() => setState((state) => ({ ...state, CCDOpen: true }))}
        />
        <DialogContent>
          <LoadingOverlay
            loading={state.loading}
            error={state.error}
          >
            <Grid style={{ display: "flex", gap: "20px" }}>
              <DocumentViewerContainer>
                {template && (
                  <DocumentViewer
                    fileDto={template}
                    embedded
                    defaultMainScale={0.8}
                    hideHeader
                  />
                )}
              </DocumentViewerContainer>
              <Container>
                <Table>
                  <StyledTableHead>
                    <TableRow>
                      <TableCell align="center">{`${t(`${tRP}.placeholder`)}`}</TableCell>
                      <TableCell align="center">{`${t(`${tRP}.placeholder_field`)}`}</TableCell>
                      <TableCell align="center">{`${t(`${tRP}.link_unlink`)}`}</TableCell>
                    </TableRow>
                  </StyledTableHead>
                  <TableBody>
                    {state.placeholders
                      .sort((a, b) => {
                        return a.index - b.index;
                      })
                      .map((placeholder) => (
                        <DocumentTemplateSinglePlaceholderConfig
                          key={placeholder.id}
                          placeholder={placeholder}
                          handleUpdatePlaceholder={handleUpdatePlaceholder}
                          allPlaceholders={state.placeholders}
                        />
                      ))}
                  </TableBody>
                </Table>
                {/* <PlaceholdersContainer>
                  
                </PlaceholdersContainer> */}
              </Container>
            </Grid>
          </LoadingOverlay>
        </DialogContent>

        <DialogActions
          handleCancel={() => setState((state) => ({ ...state, CCDOpen: true }))}
          handleSave={handlaSaveConfig}
        />
      </StyledDialog>
      <ConfirmationChangesDialog
        open={state.CCDOpen}
        handleCancel={() => setState((state) => ({ ...state, CCDOpen: false }))}
        handleDiscard={() =>
          setState((state) => ({
            ...state,
            placeholders,
            open: false,
            CCDOpen: false,
            error: "",
          }))
        }
      />
    </>
  );
};

export default ConfigureDocumentTemplateVersionPlaceholders;
